import moment from 'moment';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import Backend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import {
  en_US as en_US_components,
  en_AU as en_AU_components,
  he_IL as he_IL_components,
  en_GB as en_GB_components,
  COMPONENTS_NAMESPACE,
} from '@jjj/components';
import { ICountry } from '@dash/country';
import {
  getCurrentCountryContext,
  getLocalStorageLanguageData,
  setLocalStorageLanguageData,
} from '../helpers/countriesHelpers';
import { COUNTRIES } from '../helpers/countries';
import { en_US, en_AU, he_IL, en_GB } from './translations';
import { languages, languagesConfig } from './config';
import { getApiUrl, isProduction } from '../settings';
import { logger } from '../helpers/logger';
import { setFirstDayOfWeek } from '../helpers/dateTime';
import { getOrganizationCode } from '../settings/organizationSettings';
import { getStoreState } from '../redux/storeAccessor';

const ns = {
  lokalise: 'lokalise',
  local: 'local', // local translations
};

const resources = {
  [languagesConfig[languages.en_US].locales.app]: {
    [ns.lokalise]: {},
    [ns.local]: en_US,
    [COMPONENTS_NAMESPACE]: en_US_components,
  },
  [languagesConfig[languages.en_AU].locales.app]: {
    [ns.lokalise]: {},
    [ns.local]: en_AU,
    [COMPONENTS_NAMESPACE]: en_AU_components,
  },
  [languagesConfig[languages.he_IL].locales.app]: {
    [ns.lokalise]: {},
    [ns.local]: he_IL,
    [COMPONENTS_NAMESPACE]: he_IL_components,
  },
  [languagesConfig[languages.en_GB].locales.app]: {
    [ns.lokalise]: {},
    [ns.local]: en_GB,
    [COMPONENTS_NAMESPACE]: en_GB_components,
  },
};

interface LocalStorageBackendOptions {
  prefix: string;
  expirationTime: number;
}

interface HttpApiBackendOptions {
  loadPath: string;
  customHeaders: () => Record<string, string>;
  parse: (response: string) => any;
}

const backendOptions: [LocalStorageBackendOptions, HttpApiBackendOptions] = [
  {
    prefix: 'i18next_res_',
    expirationTime: 15 * 60 * 1000, // 15 minutes
  },
  {
    loadPath: `${getApiUrl()}/translations/{{lng}}.json`,
    customHeaders: () => {
      let isUnifiedLoginEnabled = false;

      try {
        isUnifiedLoginEnabled = Boolean(getStoreState()?.FeatureFlags?.flags?.unified_login_enabled);
      } catch (error) {
        logger.warn(`Unable to read store state in i18n customHeaders: ${error}`);
      }

      return {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'OS-Type': 'web',
        ...(!isUnifiedLoginEnabled && { 'ORGANIZATION-CODE': getOrganizationCode() }),
      };
    },
    parse: (response: string) => (response ? JSON.parse(response) : null),
  },
];

export function getCurrentLanguage() {
  return i18next.language;
}

const i18nextOptions = {
  debug: false,
  lng: getLocalStorageLanguageData()?.language || getCurrentCountryContext().locale || languages.en_US,
  ns: [ns.lokalise, ns.local],
  defaultNS: ns.lokalise,
  fallbackNS: ns.local,
  fallbackLng: languages.en_US,
  keySeparator: '.',
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
    wait: true,
  },
  load: 'currentOnly' as 'currentOnly', // 'all' | 'currentOnly' | 'languageOnly'
  // https://github.com/i18next/i18next-http-backend
  backend: {
    backends: [
      LocalStorageBackend, // primary
      HttpApi, // fallback
    ],
    backendOptions,
  },
};

i18next
  .use(Backend)
  .use(initReactI18next)
  .init(i18nextOptions, error => {
    if (error) {
      logger.error(error);
    } else {
      setFirstDayOfWeek();
    }
  });

// Load local translations (for fallback)
Object.keys(languagesConfig).forEach(language => {
  i18next.addResourceBundle(
    language,
    ns.local,
    resources[language][ns.local],
    true, // deep
    false, // overwrite
  );

  i18next.addResourceBundle(
    language,
    ns.local,
    resources[language][COMPONENTS_NAMESPACE],
    true, // deep
    false, // overwrite
  );
});

i18next.on('languageChanged', language => {
  if (languagesConfig[language]) {
    const locale = language === 'he-IL' ? 'he' : language;
    moment.locale(locale);
    setFirstDayOfWeek();
  }
});

// eslint-disable-next-line @typescript-eslint/default-param-last
export function t(key: string, options: any = {}, fallback?: string, errorReporting: boolean = true) {
  if (!key) {
    return '';
  }

  const exists = i18next.exists(key);

  if (!exists) {
    if (errorReporting) {
      logger.error(`Translation key is missing for ${key}.`);
    }

    if (fallback) {
      return fallback;
    }
  }

  let result = i18next.t(key, { ...options });

  // replace translations by '*' for testing purposes
  if (!isProduction) {
    if (localStorage.getItem('testTranslations') === 'true') {
      result = result.replace(/./g, '*');
    }
  }

  return result;
}

export function setLanguage(language: string) {
  setLocalStorageLanguageData('language', language);
  setLocalStorageLanguageData('name', languagesConfig[language].name);
  i18next.changeLanguage(language);
}

export function setGoogleLanguage() {
  setLocalStorageLanguageData('googleLanguage', getCurrentCountryContext().googleLanguage);
}

export function getLanguageByCountry(country = getCurrentCountryContext()) {
  return COUNTRIES[country.name]?.locale ?? i18nextOptions.lng;
}

export function setLanguageByCountry(country: ICountry) {
  return setLanguage(getLanguageByCountry(country));
}

export const getLanguageDirection = () => {
  return localStorage.getItem('dir') || i18next.dir();
};

export const translateOptionLabel = (fieldName: string, optionId: string, fallbackTranslation?: string) => {
  const key = `dash.common.select_options.${fieldName}.${optionId}_label`;
  const exists = i18next.exists(key);

  if (!exists) {
    logger.error(`Translation key is missing for ${key}. Used fallback: ${fallbackTranslation}`);

    return fallbackTranslation || optionId;
  }

  return t(key);
};

export default i18next;
